/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useState, useEffect, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

import { v4 as uuidv4 } from "uuid";

// react-table components
import { useTable, usePagination, useSortBy } from "react-table";
import { useSelector } from "react-redux";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Material Dashboard 2 PRO React examples
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

// Material Dashboard 2 PRO React contexts
import { setPlanningRows } from "redux/reducers/filtersReducer";
import { useMaterialUIController } from "context";
function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  userList = null,
  foremans = null,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [planningUsers, setPlanningUsers] = useState([]);

  const todayRef = useRef(null);
  const firstMonthDayRef = useRef(null);
  const tableContainerRef = useRef(null); // Add this line to create a ref for the table container

  useEffect(() => {
    if (todayRef.current && tableContainerRef.current) {
      todayRef.current.style.backgroundColor = "#e5e5e5"; // Temporarily highlight the column
    }

    const executeScroll = () => {
      if (firstMonthDayRef.current && tableContainerRef.current) {
        const scrollableContainer = tableContainerRef.current;
        const targetElement = firstMonthDayRef.current;

        // Calculate horizontal scroll position
        const leftOffset =
          targetElement.getBoundingClientRect().left +
          scrollableContainer.scrollLeft -
          scrollableContainer.getBoundingClientRect().left -
          275;

        scrollableContainer.scrollTo({
          left: leftOffset,
          behavior: "smooth",
        });
      }
    };

    // Execute scroll after a slight delay to ensure elements are rendered
    const timer = setTimeout(() => {
      executeScroll();
    }, 100); // Adjust delay as needed

    return () => clearTimeout(timer);
  }, []); // Dependency array might need adjustment based on your data loading logic

  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const planningRows = useSelector((state) => state.filters.planningRows);
  const [filteredData, setFilteredData] = useState(data); // Add this state to hold filtered data

  const tableInstance = useTable(
    { columns, data: filteredData, initialState: { pageIndex: 0 } },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance;

  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 30;

  // Update filtered data when selectedUsers changes
  useEffect(() => {
    setFilteredData(canSearch ? filterData(data, planningRows) : data);
  }, [planningRows, data, canSearch]);

  const filterData = (data, selectedOptions) => {
    if (selectedOptions.length === 0) return data;
    return data.filter((row) => {
      const userName = row["users"].props.children.props.children;
      const userArea = row["users"].props.children.props.area;
      const userForemans = row["users"].props.children.props.foremans;
      const userRole = row["users"].props.children.props.role;

      // Convert userForemans string into an array if it's not already an array
      const foremanArray =
        typeof userForemans === "string"
          ? userForemans.split(",").map((id) => id.trim())
          : userForemans;

      // userArea may be a single string (legacy) or an array (multi-zone users)
      const userAreasArray = Array.isArray(userArea)
        ? userArea
        : typeof userArea === "string" && userArea.startsWith("[")
        ? (() => {
            try {
              return JSON.parse(userArea);
            } catch {
              return [userArea];
            }
          })()
        : userArea
        ? [userArea]
        : [];

      return selectedOptions.find(
        (option) =>
          option.name === userName ||
          (option.group === "Zones géographiques" &&
            userAreasArray.includes(option.name) &&
            userRole === "Technicien") ||
          (option.group === "Contremaîtres" && foremanArray.includes(String(option.id)))
      );
    });
  };

  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "30", "50"];

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 30), [defaultValue]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => setPageSize(value);

  // Render the paginations
  const renderPagination = pageOptions.map((option) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }) =>
    value > pageOptions.length || value < 0 ? gotoPage(0) : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }) => gotoPage(Number(value.value - 1));

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Setting the entries starting point
  const entriesStart = pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) {
    entriesEnd = pageSize;
  } else if (pageIndex === pageOptions.length - 1) {
    entriesEnd = rows.length;
  } else {
    entriesEnd = pageSize * (pageIndex + 1);
  }

  return (
    <TableContainer
      id={canSearch && "ressources-scheduler-container"}
      ref={tableContainerRef}
      sx={{ 
        boxShadow: "none",
        overflowX: "auto",
        overflowY: "visible", // This is important
        maxHeight: "calc(100vh - 300px)", // Adjust as needed
      }}    >
      <Table id={canSearch && "ressources-scheduler"} {...getTableProps()}>
        <MDBox component="thead" sx={{ zIndex: 3, position: "sticky", top: 0, backgroundColor: "white !important"}}>
          {headerGroups.map((headerGroup) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} >
              {headerGroup.headers.map((column) => {
                return (
                  <DataTableHeadCell
                    isOddWeek={column?.isOddWeek}
                    length={headerGroup.headers?.length}
                    canSearch={canSearch}
                    foremans={foremans}
                    userList={userList}
                    planningRows={planningRows}
                    setPlanningRows={setPlanningRows}
                    planningUsers={planningUsers}
                    setPlanningUsers={setPlanningUsers}
                    original={column?.originalId}
                    info={column?.info}
                    accessor={column?.id}
                    rows={rows && rows.length}
                    colspan={column?.colspan}
                    {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                    width={column.width ? column.width : "auto"}
                    align={column.align ? column.align : "left"}
                    todayRef={todayRef && todayRef}
                    firstMonthDayRef={firstMonthDayRef && firstMonthDayRef}
                    sorted={setSortedValue(column)}
                  >
                    {column.render("Header")}
                  </DataTableHeadCell>
                );
              })}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <DataTableBodyCell
                    isOddWeek={cell.column.isOddWeek} // Pass the isOddWeek flag
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Affichage de {entriesStart} à{"  "}
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "4rem", display: "inline-flex" }} // Apply inline-flex here
                renderInput={(params) => <MDInput {...params} />}
              />
              {"  "}
              sur {rows.length} lignes
            </MDTypography>
          </MDBox>
        )}
        {pageOptions.length > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            <MDPagination disabled={!canPreviousPage} item onClick={() => previousPage()}>
              <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
            </MDPagination>
            {renderPagination.length > 6 ? (
              <MDBox mx={1}>
                <MDInput
                  size="small"
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex]}
                  onChange={(handleInputPagination, handleInputPaginationValue)}
                />
              </MDBox>
            ) : (
              renderPagination
            )}

            <MDPagination disabled={!canNextPage} item onClick={() => nextPage()}>
              <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
            </MDPagination>
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
};

export default DataTable;
